import React, { useState, useEffect, useMemo, useRef } from "react";
import TickerDesktop from "./TickerDesktop";
import TickerDesktopFocus from "./TickerDesktopFocus";
import TickerMobile from "./TickerMobile";
import TickerMobileFocusFilterBar from "./TickerMobileFocusFilterBar";
import FocusTickerSkeleton from "../skeleton/FocusTickerSkeleton";
import { watchMediaQuery } from "../../utils/mediaQuery";

const TICKER_ITEM_LIMIT = 10;

const getTickerItems = (items = []) => items.slice(0, TICKER_ITEM_LIMIT);

const Ticker = ({
  news = [],
  focusNews = [],
  onNewsClick,
  isFocusMode,
  focusSource,
  activeSources = [],
  favicons = {},
  isModalOpen = false,
  onOpenModal,
  isMobile = false,
  isSourcePanelOpen = false,
  onFocusSourceChange,
  showDesktopFocusSkeleton = false,
}) => {
  const [paused, setPaused] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(isMobile);

  const showFocusOverlay = Boolean(isFocusMode && focusSource);

  const wasModalOpenRef = useRef(isModalOpen);
  useEffect(() => {
    return watchMediaQuery("(max-width: 767px)", setIsMobileDevice);
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      setPaused(true);
    }

    if (wasModalOpenRef.current && !isModalOpen) {
      setPaused(false);

      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => setPaused(false));
      }
    }

    wasModalOpenRef.current = isModalOpen;
  }, [isModalOpen]);

  /*
  |--------------------------------------------------------------------------
  | Keep Home and Focus ticker datasets independent.
  |--------------------------------------------------------------------------
  |
  | The hidden Home ticker always receives only `news`.
  | The Focus ticker always receives only `focusNews`.
  | No cached snapshot is shared between modes.
  |
  */
  const randomNews = useMemo(() => {
    return getTickerItems(news);
  }, [news]);

  const focusRandomNews = useMemo(() => {
    return getTickerItems(focusNews);
  }, [focusNews]);

  const handleTickerItemClick = (items, item, event) => {
    event?.preventDefault();
    event?.stopPropagation();

    setPaused(true);

    if (onOpenModal) {
      onOpenModal({ items, clickedItem: item });
      return;
    }

    onNewsClick?.(item, event);
  };

  const handleItemClick = (item, event) => {
    handleTickerItemClick(randomNews, item, event);
  };

  const handleFocusItemClick = (item, event) => {
    handleTickerItemClick(focusRandomNews, item, event);
  };

  const showSourceIcons =
    isMobileDevice && isSourcePanelOpen && !isModalOpen;

  if (isMobileDevice) {
    return (
      <>
        <TickerMobile
          showSourceIcons={showSourceIcons && !showFocusOverlay}
          activeSources={activeSources}
          favicons={favicons}
          randomNews={randomNews}
          paused={paused || showFocusOverlay}
          setPaused={setPaused}
          onItemClick={handleItemClick}
          isMobileDevice={isMobileDevice}
          isHidden={showFocusOverlay}
        />

        <TickerMobileFocusFilterBar
          focusSource={focusSource}
          activeSources={activeSources}
          favicons={favicons}
          onSourceSelect={onFocusSourceChange}
          isActive={showFocusOverlay}
        />

      </>
    );
  }

  if (!isMobileDevice) {
    return (
      <>
        <TickerDesktop
          randomNews={randomNews}
          paused={paused || showFocusOverlay}
          setPaused={setPaused}
          onItemClick={handleItemClick}
          lockPaused={isModalOpen}
          isHidden={showFocusOverlay}
        />

        {showFocusOverlay && showDesktopFocusSkeleton ? (
          <FocusTickerSkeleton />
        ) : (
          <TickerDesktopFocus
            randomNews={focusRandomNews}
            paused={paused}
            setPaused={setPaused}
            onItemClick={handleFocusItemClick}
            lockPaused={isModalOpen}
            isActive={showFocusOverlay}
          />
        )}

      </>
    );
  }

  return null;
};

export default Ticker;
